import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faCircleInfo } from "@fortawesome/sharp-regular-svg-icons"
import { Box, Chip, Divider, Grid, List, ListItemButton, Tooltip, Typography } from "@mui/material"
import React, { PropsWithChildren, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { StandardDialog } from "../dialogs/StandardDialog"
import { useExternalLinks } from "../../hooks/useAppLinks"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { fetchPageHelpContent, Page, PageHelpContent, PageHelpStatus } from "./PageHeaderApi"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { MaintenanceContent } from "../../layouts/components/MaintenanceContent"
import { isEmpty } from "lodash"

const useStyles = makeStyles()((theme) => ({
    pageHeaderArea: {
        marginTop: "2rem",
        marginBottom: "2rem",
    },
    breadcrumbs: {
        paddingBottom: "0.5rem",
    },
    pageHeader: {
        width: "100%",
        marginTop: "1rem",
    },
    titleArea: {
        width: "auto",
        gap: "0.5rem",
    },
    title: {
        paddingRight: "0.5rem",
        color: theme.palette.tailwind.slate[600],
        fontWeight: 400,
    },
    tooltip: {
        "&:hover": {
            cursor: "pointer",
        },
    },
    dialogContainer: {
        padding: ".25rem 5rem 7rem 5rem",
    },
    chip: {
        fontWeight: 500,
        marginLeft: ".5rem",
        borderRadius: ".5rem",
        color: theme.palette.common.white,
    },
    chipGreen: {
        background: theme.palette.tailwind.green[600],
    },
    chipDark: {
        backgroundColor: theme.palette.common.black,
    },
    helpTitle: {
        fontWeight: 600,
        fontSize: "1.563rem",
        lineHeight: 1.17,
        marginTop: "3.25rem",
        marginBottom: "1.5rem",
        color: theme.palette.tailwind.slate[700],
    },
    articleTitle: {
        fontWeight: 400,
        fontSize: "1.25rem",
        lineHeight: "1.25rem",
        color: theme.palette.tailwind.slate[600],
    },
    arrowIcon: {
        color: theme.palette.tailwind.slate[600],
        marginLeft: "0.5rem",
        justifyContent: "end",
    },
    listButton: {
        padding: "1rem",
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
    },
    dialogTitle: {
        color: theme.palette.tailwind.slate[700],
        fontWeight: 600,
        fontSize: "2.5rem",
        lineHeight: 1.17,
        textAlign: "left",
    },
    updateDialogTitle: {
        justifyContent: "start",
        padding: "0 4.5rem",
    },
}))

export type PageHeaderProps = Readonly<
    PropsWithChildren<{
        page: Page
        title: string
        icon?: JSX.Element
        rightElement?: JSX.Element
    }>
>
const selectHelpContent = (data: PageHelpContent) => data?.pageHelp || {}

export function PageHeader({ children, page, title, icon, rightElement }: PageHeaderProps) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const [showModal, setShowModal] = useState(false)

    const { data: pageHelpContent } = useApiQuery(fetchPageHelpContent, {
        queryName: "get-help-page-content",
        dependencies: {
            page,
        },
        select: selectHelpContent,
    })

    const handleExternalLink = useExternalLinks()

    return (
        <>
            <Grid container direction="column" wrap="nowrap" className={classes.pageHeaderArea}>
                {children && <Box className={classes.breadcrumbs}>{children}</Box>}
                <Grid container justifyContent="space-between" alignItems="center" className={classes.pageHeader}>
                    <Grid item container alignItems="center" className={classes.titleArea}>
                        <Typography variant="h3" display="inline" className={classes.title}>
                            {title}
                        </Typography>
                        {!isEmpty(pageHelpContent) && (
                            <>
                                <Tooltip
                                    className={classes.tooltip}
                                    title="Click here for more info"
                                    onClick={() => setShowModal(true)}
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} size={"lg"} style={{ color: "GrayText" }} />
                                </Tooltip>
                                {pageHelpContent?.status && pageHelpContent.status !== PageHelpStatus.MAINTENANCE && (
                                    <Chip
                                        label={l10n.getStringForEnum(
                                            "page-header-help-status-mode",
                                            pageHelpContent?.status,
                                            "",
                                        )}
                                        className={cx(
                                            classes.chip,
                                            pageHelpContent?.status === PageHelpStatus.NEW
                                                ? classes.chipGreen
                                                : classes.chipDark,
                                        )}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                    {rightElement && <Grid item>{rightElement}</Grid>}
                </Grid>
            </Grid>
            {pageHelpContent?.status && pageHelpContent.status === PageHelpStatus.MAINTENANCE && <MaintenanceContent />}
            <StandardDialog
                open={showModal}
                onClose={() => setShowModal(false)}
                dialogTitle={<Typography className={classes.dialogTitle}>{title}</Typography>}
                maxWidthProp="md"
                updateDialogClasses={{
                    dialogTitleContainer: classes.updateDialogTitle,
                }}
            >
                <Grid
                    container
                    justifyContent="center"
                    alignItems="flex-start"
                    direction="column"
                    wrap="wrap"
                    className={classes.dialogContainer}
                >
                    <Typography variant="body1" className={classes.articleTitle}>
                        {pageHelpContent?.text}
                    </Typography>
                    {(pageHelpContent?.helpGuides?.length ?? 0) > 0 && (
                        <>
                            <Grid item>
                                <LocalizedStrict id="page-header-help-text">
                                    <Typography variant="h6" className={classes.helpTitle}>
                                        Help guides
                                    </Typography>
                                </LocalizedStrict>
                            </Grid>

                            <List sx={{ width: "100%", padding: 0, bgcolor: "background.paper" }} component="nav">
                                {pageHelpContent?.helpGuides.map((article, index) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <ListItemButton
                                                className={classes.listButton}
                                                onClick={() => handleExternalLink(article.url)}
                                            >
                                                <Box>
                                                    <Typography className={classes.articleTitle}>
                                                        {article?.title}
                                                    </Typography>
                                                </Box>
                                                <FontAwesomeIcon icon={faChevronRight} className={classes.arrowIcon} />
                                            </ListItemButton>
                                            <Divider />
                                        </React.Fragment>
                                    )
                                })}
                            </List>
                        </>
                    )}
                </Grid>
            </StandardDialog>
        </>
    )
}
